import { FC, useState } from 'react';

import { RichMediaState } from '@bonusly/common-action-input';
import {
	Button,
	FormControl,
	FormLabel,
	HStack,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	useColorModeValue,
	useToast,
	VStack,
} from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';

import { useFetchRecipients } from '../../../../components/recognition/give-box/hooks/use-fetch-recipients';
import { Goal } from '../../../../types/goal';
import { useUpdateGoal } from '../../hooks/use-goals';
import { CollaboratorSelector } from './collaborator-selector';

interface GoalCollaboratorManagerProps {
	isOpen: boolean;
	onClose: () => void;
	goal: Goal | null;
	onSuccess?: () => void;
}

export const GoalCollaboratorManager: FC<GoalCollaboratorManagerProps> = ({ isOpen, onClose, goal, onSuccess }) => {
	const [selectedCollaborators, setSelectedCollaborators] = useState<Recipient[]>([]);
	const toast = useToast();
	const updateGoal = useUpdateGoal();
	const bgColor = useColorModeValue('white', 'gray.800');
	const borderColor = useColorModeValue('gray.200', 'gray.600');

	// Real search function using useFetchRecipients hook
	const fetchRecipients = useFetchRecipients({
		include_self: false,
		include_non_receivable: true,
		search_usergroups: false,
		search_users: true,
	});

	const searchCollaborators = async (query: string): Promise<Recipient[]> => {
		const emptyState = {
			mentions: [],
			recipients: [],
			hashtags: [],
			json: null,
			plainText: '',
			text: '',
		} as unknown as RichMediaState;

		const results = await fetchRecipients({
			query,
			state: emptyState,
		});

		// Filter out existing collaborators
		const existingCollaboratorIds = goal?.collaborators.map((collaborator) => collaborator.id) || [];
		return results.filter((recipient) => !existingCollaboratorIds.includes(recipient.id));
	};

	const handleClose = () => {
		setSelectedCollaborators([]);
		onClose();
	};

	const handleSave = async () => {
		if (!goal || selectedCollaborators.length === 0) return;

		try {
			// Get current collaborator IDs
			const currentCollaboratorIds = goal.collaborators?.map((collaborator) => collaborator.id) || [];

			// Add new collaborator IDs
			const newCollaboratorIds = selectedCollaborators.map((c) => c.id);
			const updatedCollaboratorIds = [...currentCollaboratorIds, ...newCollaboratorIds];

			await updateGoal.mutateAsync({
				id: goal.id,
				data: { collaborators: updatedCollaboratorIds },
			});

			toast({
				title: 'Collaborators added successfully!',
				status: 'success',
				duration: 3000,
			});

			onSuccess?.();
			handleClose();
		} catch (error) {
			console.error('Failed to add collaborators:', error);
			toast({
				title: 'Failed to add collaborators',
				description: error instanceof Error ? error.message : 'Unknown error occurred',
				status: 'error',
				duration: 3000,
			});
		}
	};

	const handleAddCollaborator = (collaborator: Recipient) => {
		if (!selectedCollaborators.find((c) => c.id === collaborator.id)) {
			setSelectedCollaborators((prev) => [...prev, collaborator]);
		}
	};

	const handleRemoveCollaborator = (collaborator: Recipient) => {
		setSelectedCollaborators((prev) => prev.filter((c) => c.id !== collaborator.id));
	};

	if (!goal) {
		return null;
	}

	const hasNewCollaborators = selectedCollaborators.length > 0;

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
			<ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
			<ModalContent bg={bgColor} borderRadius="xl" border="1px solid" borderColor={borderColor}>
				<ModalHeader pb={3}>
					<Text fontSize="xl" fontWeight="semibold">
						Add collaborators
					</Text>
				</ModalHeader>
				<ModalCloseButton />

				<ModalBody py={6}>
					<VStack spacing={4} align="stretch">
						<Text fontSize="sm" color="gray.600">
							Add people who will help you achieve this goal.
						</Text>

						<FormControl>
							<FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
								Search for collaborators
							</FormLabel>
							<CollaboratorSelector
								value={selectedCollaborators}
								onSearch={searchCollaborators}
								onSelect={handleAddCollaborator}
								onRemove={handleRemoveCollaborator}
								placeholder="Type to search for people..."
							/>
						</FormControl>

						{selectedCollaborators.length > 0 && (
							<VStack align="start" spacing={2}>
								<Text fontSize="sm" fontWeight="medium" color="gray.700">
									{selectedCollaborators.length} collaborator{selectedCollaborators.length > 1 ? 's' : ''} to be added:
								</Text>
								{selectedCollaborators.map((collaborator) => (
									<Text key={collaborator.id} fontSize="sm" color="gray.600">
										• {collaborator.displayName}
									</Text>
								))}
							</VStack>
						)}
					</VStack>
				</ModalBody>

				<ModalFooter>
					<HStack spacing={3}>
						<Button variant="ghost" onClick={handleClose} color="gray.600">
							Cancel
						</Button>
						<Button colorScheme="primary" onClick={handleSave} isDisabled={!hasNewCollaborators} isLoading={updateGoal.isLoading} loadingText="Adding...">
							Save
						</Button>
					</HStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
